import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { clsx } from 'clsx';
import { MapPin, XCircle } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { integrationsApi } from '../api/integrationsApi';
import { integrationKeys } from '../queryKeys';
import { Button } from '@/shared/ui/Button';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader } from '@/shared/ui';
import styles from './GoogleAdsSetupPage.module.css';

export function GBPSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const tempToken = searchParams.get('t') ?? '';
  const [selectedListing, setSelectedListing] = useState<{
    accountId: string;
    locationName: string;
    displayName: string;
  } | null>(null);
  const [error, setError] = useState('');

  const listingsQuery = useQuery({
    queryKey: integrationKeys.gbpListings(tempToken),
    queryFn: () => integrationsApi.gbp.getListings(tempToken).then((r) => r.data.listings),
    enabled: !!tempToken,
  });

  const setupMutation = useMutation({
    mutationFn: () => {
      if (!selectedListing || !currentLocation) throw new Error('No listing or location selected');
      return integrationsApi.gbp.completeSetup(
        currentLocation.id,
        selectedListing.accountId,
        selectedListing.locationName,
        selectedListing.displayName,
        tempToken,
      );
    },
    onSuccess: () => navigate('/settings/organic?connected=gbp', { replace: true }),
    onError: (err: Error) =>
      setError(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          'Failed to connect Google Business Profile',
      ),
  });

  if (!tempToken) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>
            <XCircle size={40} strokeWidth={1.5} aria-hidden />
          </div>
          <h1>Invalid Setup Link</h1>
          <p>This link is missing required authorization data.</p>
          <Button onClick={() => navigate('/settings/organic')}>
            Back to Organic & Offline Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <MapPin size={40} strokeWidth={1.5} aria-hidden />
        </div>
        <PageHeader
          title="Connect Google Business Profile"
          subtitle={
            <>
              Select the listing for <strong>{currentLocation?.name ?? 'this location'}</strong>.
            </>
          }
          align="center"
        />

        {listingsQuery.isLoading && <SkeletonCard />}
        {listingsQuery.isError && (
          <p className={styles.errorMessage}>
            Failed to load your GBP listings:{' '}
            {(listingsQuery.error as AxiosError<{ error?: { message?: string } }>)?.response?.data
              ?.error?.message ??
              (listingsQuery.error as Error)?.message ??
              'Please try again.'}
          </p>
        )}

        {listingsQuery.data && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              setupMutation.mutate();
            }}
            className={styles.form}
          >
            <div className={styles.pickerList}>
              {listingsQuery.data.length === 0 && (
                <p className={styles.pickerEmpty}>No GBP listings found on this Google account.</p>
              )}
              {listingsQuery.data.map((listing) => (
                <label
                  key={listing.locationName}
                  className={clsx(
                    styles.pickerItem,
                    styles.pickerItemStart,
                    selectedListing?.locationName === listing.locationName &&
                      styles.pickerItemActive,
                  )}
                >
                  <input
                    type="radio"
                    name="listing"
                    value={listing.locationName}
                    checked={selectedListing?.locationName === listing.locationName}
                    onChange={() => setSelectedListing(listing)}
                    className={styles.pickerInput}
                  />
                  <div>
                    <div className={styles.pickerName}>{listing.displayName}</div>
                    {listing.address && <div className={styles.pickerMeta}>{listing.address}</div>}
                    {listing.phone && <div className={styles.pickerMetaSub}>{listing.phone}</div>}
                  </div>
                </label>
              ))}
            </div>

            {error && <p className={styles.errorMessage}>{error}</p>}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/settings/organic')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!selectedListing} isLoading={setupMutation.isPending}>
                Connect
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
